import React, { useEffect, useRef } from 'react';

import { WavyBackgroundProps } from '../models';

const WavyBackground: React.FC<WavyBackgroundProps> = ({
  src,
  amp = 8,
  len = 180,
  speed = 1.4,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const startRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current!;
    const ctx = canvas.getContext('2d')!;
    const img = new Image();
    img.src = src;
    imgRef.current = img;

    // --- Resize canvas to wrapper size ---
    const resize = () => {
      const parent = canvas.parentElement!;
      const rect = parent.getBoundingClientRect();

      canvas.width = rect.width;
      canvas.height = rect.height;
    };

    resize();
    window.addEventListener('resize', resize);

    const draw = (t: number) => {
      if (!startRef.current) startRef.current = t;
      const time = (t - startRef.current) / 1000;

      if (!img.complete) {
        rafRef.current = requestAnimationFrame(draw);

        return;
      }

      const w = canvas.width;
      const h = canvas.height;

      ctx.clearRect(0, 0, w, h);

      // --- PROPORTIONAL SCALING (fix for big images like 1700×954) ---
      const scale = w / img.width;
      const scaledH = img.height * scale;

      // visible part — no stretch
      const visibleH = Math.min(scaledH, h);

      for (let y = 0; y < visibleH; y += 1) {
        const srcY = y / scale; // map canvas row → image row

        const offsetX = Math.sin((y / len) * Math.PI * 3 + time * speed) * amp;

        ctx.drawImage(
          img,
          0,
          srcY,
          img.width,
          1 / scale, // one image pixel high (adjusted for scale)
          offsetX,
          y,
          w,
          1
        );
      }

      rafRef.current = requestAnimationFrame(draw);
    };

    rafRef.current = requestAnimationFrame(draw);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', resize);
    };
  }, [src, amp, len, speed]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 0,
      }}
    />
  );
};

export default WavyBackground;
